import React, {Fragment, useEffect, useState} from "react";
import {Box} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {useTranslation} from "react-i18next";
import {connect, useSelector} from "react-redux";
import {v4 as uuidv4} from "uuid";
import Call from "../../../hocs/call";
import FullscreenProvider, {useFullscreen} from "../../../hocs/fullscreen-provider";
import AlertDialog from "../../alert-dialog";
import SingleViewerFooter from "./Footer";
import SingleViewerHeader from "./Header";
import SingleViewerSidebar from "./Sidebar";
import SingleViewerViewer from "./Viewer";
import {
  backupDatasetSVState,
  fetchDatasetSVDataset,
  hideDatasetSVDownloadWarning,
  hideDatasetSVUnavailableViewWarning
} from "../../../state/dataset/single-viewer/actions";
import {themeConfigShowStatViewerFooter} from "../../../state/theme-config/themeConfigSelectors";
import {getDatasetStorageKey} from "../../../utils/other";

const $ = window.jQuery;

const mapStateToProps = ({dataset}) => ({
  datasetUuid: dataset.singleViewer.datasetUuid,
  isFetchDatasetDisabled: dataset.singleViewer.isFetchDatasetDisabled,
  criteria: dataset.singleViewer.criteria,
  isDownloadWarningVisible: dataset.singleViewer.isDownloadWarningVisible,
  isUnavailableViewWarningVisible: dataset.singleViewer.isUnavailableViewWarningVisible,
  lastNObservations: dataset.singleViewer.lastNObservations
});

const mapDispatchToProps = dispatch => ({
  onDatasetFetch: ({nodeId, packageId, datasetId, datasetTitle, criteria, lastNObservations}) =>
    dispatch(fetchDatasetSVDataset(nodeId, packageId, datasetId, datasetTitle, criteria, lastNObservations)),
  onDownloadWarningHide: () => dispatch(hideDatasetSVDownloadWarning()),
  onUnavailableViewHide: () => dispatch(hideDatasetSVUnavailableViewWarning()),
  onDatasetBackup: datasetUuid => dispatch(backupDatasetSVState(datasetUuid))
});

const handleStyle = () => {
  const headerHeight = document.getElementById("data-viewer__header")?.offsetHeight || 0;
  const footerHeight = document.getElementById("data-viewer__footer")?.offsetHeight || 0;
  $("#data-viewer__sidebar-viewer-container").height(`calc(100% - ${headerHeight + footerHeight}px)`);

  const sidebarWidth = document.getElementById("data-viewer__sidebar")?.offsetWidth || 0;
  $("#data-viewer__viewer").width(`calc(100% - ${sidebarWidth}px)`);
};

function SingleViewerComponent(props) {
  const {
    nodeId,
    nodeCode,
    categoryPath,
    packageId,
    packageMetadata,
    packageApproved,
    datasetId,
    datasetTitle,
    datasetSource,
    datasetLastUpdate,
    datasetNotes,
    datasetMetadataUrl,
    viewId,
    attachedFiles,
    nodeExtras,

    datasetUuid,
    isFetchDatasetDisabled,
    criteria,
    isDownloadWarningVisible,
    isUnavailableViewWarningVisible,
    lastNObservations,

    onDatasetFetch,
    onDownloadWarningHide,
    onUnavailableViewHide,
    onDatasetBackup
  } = props;

  const {t} = useTranslation();

  const {isFullscreen, handleFullscreen} = useFullscreen();

  const showFooter = useSelector(themeConfigShowStatViewerFooter);

  const [chartId] = useState("chart__" + uuidv4());

  const [mapId] = useState("map__" + uuidv4());

  useEffect(() => {
    return () => {
      const storedDatasets = JSON.parse(sessionStorage.getItem("datasets") || "[]");
      if (datasetUuid && storedDatasets.map(getDatasetStorageKey).includes(datasetUuid)) {
        onDatasetBackup(datasetUuid);
      }
    };
  }, [datasetUuid, onDatasetBackup]);

  useEffect(() => {
    window.addEventListener("resize", handleStyle);
    return () => window.removeEventListener("resize", handleStyle);
  }, []);

  useEffect(() => {
    handleStyle();
  });

  return (
    <Fragment>
      <Box
        id="data-viewer"
        sx={{
          width: "100%",
          height: "100%"
        }}
        className={` data-viewer__single-viewer`}
      >
        <Call
          cb={onDatasetFetch}
          cbParam={{
            nodeId,
            packageId,
            datasetId,
            datasetTitle,
            criteria,
            lastNObservations
          }}
          disabled={!criteria || isFetchDatasetDisabled}
        >
          <Box
            id="data-viewer__header"
            sx={{
              width: "100%",
              padding: isFullscreen ? 0 : "0 16px",
              marginTop: isFullscreen ? "-16px" : 0
            }}
          >
            <SingleViewerHeader
              nodeId={nodeId}
              nodeCode={nodeCode}
              nodeExtras={nodeExtras}
              categoryPath={categoryPath}
              packageId={packageId}
              packageApproved={packageApproved}
              datasetId={datasetId}
              datasetTitle={datasetTitle}
              datasetSource={datasetSource}
              datasetLastUpdate={datasetLastUpdate}
              datasetNotes={datasetNotes}
              viewId={viewId}
              attachedFiles={attachedFiles}
              chartId={chartId}
              mapId={mapId}
              onRender={handleStyle}
              onToggleFullScreen={handleFullscreen}
              isFullscreen={isFullscreen}
            />
          </Box>
          <Box
            id="data-viewer__sidebar-viewer-container"
            sx={{
              display: "flex",
              width: "100%",
              height: "100%",
              minHeight: "400px",
              minWidth: "560px"
            }}
            style={{padding: isFullscreen ? "0" : "0 16px"}}
          >
            <Box id="data-viewer__sidebar" sx={{height: "100%"}} style={{display: isFullscreen ? "none" : "block"}}>
              <SingleViewerSidebar
                nodeId={nodeId}
                nodeCode={nodeCode}
                nodeExtras={nodeExtras}
                packageId={packageId}
                packageMetadata={packageMetadata}
                datasetId={datasetId}
                datasetTitle={datasetTitle}
                datasetMetadataUrl={datasetMetadataUrl}
                chartId={chartId}
                mapId={mapId}
              />
            </Box>
            <Box id="data-viewer__viewer" sx={{height: "100%"}}>
              <SingleViewerViewer
                nodeId={nodeId}
                nodeExtras={nodeExtras}
                packageId={packageId}
                datasetId={datasetId}
                datasetTitle={datasetTitle}
                chartId={chartId}
                mapId={mapId}
                isFullscreen={isFullscreen}
              />
            </Box>
          </Box>
          <Box
            id="data-viewer__footer"
            sx={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              padding: "8px"
            }}
            style={{display: isFullscreen ? "none" : "block"}}
          >
            {showFooter && (
              <SingleViewerFooter
                dataset={{
                  nodeCode: nodeCode,
                  categoryPath: categoryPath,
                  datasetId: datasetId,
                  datasetTitle: datasetTitle,
                  viewId: viewId
                }}
                onDatasetsChange={handleStyle}
              />
            )}
          </Box>
        </Call>
      </Box>

      <AlertDialog open={isDownloadWarningVisible} onClose={onDownloadWarningHide}>
        <DialogContent>{t("scenes.dataViewer.dialogs.downloadFormat.content")}</DialogContent>
        <DialogActions>
          <Button onClick={onDownloadWarningHide}>{t("commons.confirm.confirm")}</Button>
        </DialogActions>
      </AlertDialog>

      <AlertDialog
        id="data-viewer__unavailable-view-warning"
        open={isUnavailableViewWarningVisible}
        onClose={onUnavailableViewHide}
      >
        <DialogContent>{t("scenes.dataViewer.dialogs.unavailableView.content")}</DialogContent>
        <DialogActions>
          <Button id="data-viewer__unavailable-view-warning__confirm-button" onClick={onUnavailableViewHide}>
            {t("commons.confirm.confirm")}
          </Button>
        </DialogActions>
      </AlertDialog>
    </Fragment>
  );
}

const SingleViewer = props => (
  <FullscreenProvider>
    <SingleViewerComponent {...props} />
  </FullscreenProvider>
);

export default connect(mapStateToProps, mapDispatchToProps)(SingleViewer);
